import { computed, shallowRef, watch } from 'vue';
import { apiService as defaultApiService } from '@/services/api/index.js';
import { getDetectedCity as defaultGetDetectedCity, getDetectedCountry as defaultGetDetectedCountry } from '@/services/utils/userLocation.js';

export function createCanonicalDestinationSlug(destination) {
  return destination
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .trim()
    .replace(/\s+/g, '-');
}

export function createTrendingDestinationPayload({ trendingData, scope = 'local' }) {
  const regionState = trendingData.state || null;
  const country = trendingData.country || null;

  return {
    name: trendingData.name,
    destination: trendingData.name,
    location: [regionState, country].filter(Boolean).join(', '),
    country: country || '',
    state: regionState || '',
    image: trendingData.image_url || trendingData.image || '',
    image_url: trendingData.image_url || trendingData.image || '',
    rating: trendingData.appeal_score || trendingData.rating || 0,
    appeal_score: trendingData.appeal_score || trendingData.rating || 0,
    tagline: trendingData.tagline || '',
    description: trendingData.tagline || '',
    theme: trendingData.theme || '',
    estimated_cost: trendingData.price || '',
    duration: trendingData.duration || '',
    scope,
  };
}

export function useTrendingAuthFlow({
  router,
  authStore,
  apiService = defaultApiService,
  getDetectedCity = defaultGetDetectedCity,
  getDetectedCountry = defaultGetDetectedCountry,
  loadingState,
  warning,
  success,
  error,
  storage = sessionStorage,
} = {}) {
  const showAuthModal = shallowRef(false);
  const authModalMode = shallowRef('login');
  const pendingTrendingCardSearch = shallowRef(null);
  const isHandlingTrendingSearch = shallowRef(false);
  const isExecutingPendingAction = shallowRef(false);

  const hasPendingAction = computed(() => {
    return !!pendingTrendingCardSearch.value || isExecutingPendingAction.value;
  });

  const openAuthModal = (mode = 'login') => {
    authModalMode.value = mode;
    showAuthModal.value = true;
  };

  const handleOpenAuthModalEvent = (event) => {
    openAuthModal(event?.detail?.mode);
  };

  const closeAuthModal = () => {
    showAuthModal.value = false;
  };

  const normalizeTrendingSearchPayload = (eventOrPayload) => {
    return eventOrPayload?.detail ?? eventOrPayload ?? null;
  };

  const executeTrendingSearch = async ({ destination, scope, trendingData }) => {
    const destinationParts = destination.split(',').map((part) => part.trim());
    const cityName = destinationParts[0];
    const canonicalSlug = createCanonicalDestinationSlug(destination);

    try {
      if (trendingData?.name) {
        const enrichedDestination = createTrendingDestinationPayload({ trendingData, scope: scope || 'local' });

        storage.setItem(`destination_${canonicalSlug}`, JSON.stringify(enrichedDestination));

        success?.(`✈️ Loaded ${cityName}!`);
        await router.push({
          name: 'DestinationDetail',
          params: { slug: canonicalSlug },
        });
        return;
      }

      const regionState = destinationParts.length >= 3 ? destinationParts[1] : null;
      const country = destinationParts.length >= 3
        ? destinationParts[2]
        : (destinationParts.length === 2 ? destinationParts[1] : null);

      loadingState.loadingMessage.value = `Loading ${cityName}...`;
      loadingState.loadingSubMessage.value = 'Checking availability...';
      loadingState.showLoadingOverlay.value = true;
      loadingState.isTripFinderSearching.value = true;

      const apiParams = {
        mode: 'build',
        search_scope: scope,
        when_type: 'not_sure',
        user_location: {
          city: getDetectedCity(),
          country: getDetectedCountry(),
          state: null,
          coordinates: null,
          timezone: null,
        },
        destination,
        num_adults: 2,
        num_children: 0,
        num_infants: 0,
        with_pets: false,
        themes: [],
        activities: [],
        travel_methods: [],
        accommodation_types: [],
        special_requirements: [],
        max_results: 8,
      };

      const allResults = [];

      await new Promise((resolve, reject) => {
        apiService.buildTripStream(apiParams, {
          onStatus: () => {
            loadingState.loadingSubMessage.value = 'Preparing destination details...';
          },
          onBatch: (data) => {
            const results = data.options || data.suggestions || [];
            allResults.push(...results);
            loadingState.loadingSubMessage.value = 'Almost ready...';
          },
          onComplete: () => resolve(),
          onError: (streamError) => reject(new Error(streamError.error || 'Streaming failed')),
        });
      });

      if (allResults.length === 0) {
        error?.('No details found for this destination');
        return;
      }

      const cityNameLower = cityName.toLowerCase();
      const bestMatch = allResults.find((result) => {
        const resultName = (result.destination || result.name || '').toLowerCase();
        return resultName === cityNameLower
          || resultName.startsWith(cityNameLower)
          || cityNameLower.startsWith(resultName.split(' ')[0]);
      }) || allResults[0];

      const enrichedDestination = {
        ...bestMatch,
        name: bestMatch.name || bestMatch.destination || cityName,
        location: bestMatch.location || [regionState, country].filter(Boolean).join(', ') || bestMatch.country || '',
        country: bestMatch.country || country || '',
      };

      storage.setItem(`destination_${canonicalSlug}`, JSON.stringify(enrichedDestination));
      loadingState.showLoadingOverlay.value = false;

      success?.(`✈️ Loaded ${cityName}!`);
      await router.push({
        name: 'DestinationDetail',
        params: { slug: canonicalSlug },
      });
    } catch (searchError) {
      console.error('Trending card search error:', searchError);
      error?.('Failed to load destination details. Please try again.');
    } finally {
      loadingState.isTripFinderSearching.value = false;
      loadingState.showLoadingOverlay.value = false;
      isHandlingTrendingSearch.value = false;
      isExecutingPendingAction.value = false;
    }
  };

  const handleTrendingCardSearch = async (eventOrPayload) => {
    const payload = normalizeTrendingSearchPayload(eventOrPayload);

    if (!payload?.destination || isHandlingTrendingSearch.value) {
      return;
    }

    isHandlingTrendingSearch.value = true;

    if (!authStore.isAuthenticated) {
      warning?.('Please log in to view destination details');
      authModalMode.value = 'login';
      showAuthModal.value = true;
      pendingTrendingCardSearch.value = payload;
      isHandlingTrendingSearch.value = false;
      return;
    }

    await executeTrendingSearch(payload);
  };

  watch(() => authStore.isAuthenticated, async (isAuthenticated, wasAuthenticated) => {
    if (!isAuthenticated || wasAuthenticated) {
      return;
    }

    showAuthModal.value = false;

    const pendingAction = pendingTrendingCardSearch.value;
    pendingTrendingCardSearch.value = null;

    if (!pendingAction) {
      return;
    }

    isExecutingPendingAction.value = true;
    await handleTrendingCardSearch(pendingAction);
  });

  return {
    showAuthModal,
    authModalMode,
    hasPendingAction,
    pendingTrendingCardSearch,
    isExecutingPendingAction,
    openAuthModal,
    handleOpenAuthModalEvent,
    closeAuthModal,
    handleTrendingCardSearch,
  };
}
